import { getDatabase, ref, get, set, update, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import {
    ReportDraft,
    ReportDraftFilters,
    ReportDraftItem,
    ReportDraftTextBlock
} from '../types/reportDrafts';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { recordActivity } from './activityApi';

/**
 * API for admin-shared timeline report drafts at `reportDrafts/{draftKey}`.
 *
 * Admin authorization is enforced by the DB rules (admin OR superadmin) and the
 * route guard; this layer only verifies a signed-in user. All mutations stamp
 * `updatedBy` (UID) / `updatedAt` on the draft root in the same atomic update.
 * This API never writes to timeline source paths — drafts are curation-only.
 */

const DRAFTS_PATH = 'reportDrafts';

/** RTDB rejects undefined values — drop them (recursively) before writing. */
function stripUndefined<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.map(v => stripUndefined(v)) as unknown as T;
    }
    if (value && typeof value === 'object') {
        const result: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            if (v !== undefined) result[k] = stripUndefined(v);
        }
        return result as T;
    }
    return value;
}

export class ReportDraftsApi {
    private db = getDatabase();

    private getDraftPath(draftKey: string): string {
        return `${DRAFTS_PATH}/${draftKey}`;
    }

    private getCurrentUser() {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att hantera rapportutkast');
        }
        return user;
    }

    private stamp(user: { uid: string }): { updatedBy: string; updatedAt: string } {
        return { updatedBy: user.uid, updatedAt: formatDateForStorage(new Date()) };
    }

    /**
     * Guard for editor mutations: RTDB update() on a deleted path would
     * recreate a partial zombie node, so a draft deleted by another admin
     * mid-edit must fail the write instead (review round 2, F4). Checks a
     * tiny leaf to keep the extra read cheap; the remaining race window
     * (delete between check and write) is accepted.
     */
    private async assertDraftExists(draftKey: string): Promise<void> {
        const snapshot = await get(ref(this.db, `${this.getDraftPath(draftKey)}/name`));
        if (!snapshot.exists()) {
            throw new Error('Utkastet har raderats');
        }
    }

    /**
     * Validate and parse a draft from Firebase data
     */
    private parseDraft(data: unknown, key: string): ReportDraft | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;

        const filters = d.filters as Record<string, unknown> | undefined;
        if (
            typeof d.name !== 'string' ||
            typeof d.reportTitle !== 'string' ||
            typeof d.createdBy !== 'string' ||
            typeof d.createdAt !== 'string' ||
            !filters || typeof filters.startYear !== 'number' || typeof filters.endYear !== 'number'
        ) {
            console.warn(`Invalid report draft data at ${key}: missing required fields`);
            return null;
        }

        return {
            key,
            name: d.name,
            reportTitle: d.reportTitle,
            archived: d.archived === true,
            filters: filters as unknown as ReportDraftFilters,
            items: this.parseItems(d.items, key),
            textBlocks: this.parseTextBlocks(d.textBlocks, key),
            createdBy: d.createdBy,
            createdAt: d.createdAt,
            updatedBy: typeof d.updatedBy === 'string' ? d.updatedBy : undefined,
            updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : undefined,
        };
    }

    /**
     * Validate snapshot items (D14); items missing their snapshot core are
     * skipped with a warning (e.g. drafts created before the snapshot model).
     */
    private parseItems(data: unknown, draftKey: string): ReportDraft['items'] {
        if (!data || typeof data !== 'object') return undefined;
        const items: NonNullable<ReportDraft['items']> = {};
        for (const [eventId, value] of Object.entries(data as Record<string, unknown>)) {
            const item = value as Record<string, unknown> | null;
            if (!item || typeof item.included !== 'boolean' ||
                typeof item.eventType !== 'string' || typeof item.date !== 'string') {
                console.warn(`Invalid report draft item at ${draftKey}/${eventId}: missing snapshot fields`);
                continue;
            }
            items[eventId] = item as unknown as NonNullable<ReportDraft['items']>[string];
        }
        return Object.keys(items).length > 0 ? items : undefined;
    }

    /**
     * Validate text blocks on read; malformed blocks are skipped with a
     * warning, not crashed on (repo parse-on-read convention).
     */
    private parseTextBlocks(data: unknown, draftKey: string): ReportDraft['textBlocks'] {
        if (!data || typeof data !== 'object') return undefined;
        const blocks: NonNullable<ReportDraft['textBlocks']> = {};
        for (const [blockKey, value] of Object.entries(data as Record<string, unknown>)) {
            const blockData = value as Record<string, unknown> | null;
            if (!blockData || typeof blockData.text !== 'string' ||
                typeof blockData.anchorType !== 'string' || typeof blockData.sortOrder !== 'number') {
                console.warn(`Invalid report draft text block at ${draftKey}/${blockKey}: missing required fields`);
                continue;
            }
            blocks[blockKey] = { ...(blockData as unknown as ReportDraftTextBlock), key: blockKey };
        }
        return Object.keys(blocks).length > 0 ? blocks : undefined;
    }

    /**
     * Fetch all drafts (shared among admins)
     */
    async getDrafts(): Promise<{ [key: string]: ReportDraft }> {
        this.getCurrentUser();

        const snapshot = await get(ref(this.db, DRAFTS_PATH));
        if (!snapshot.exists()) return {};

        const rawData = snapshot.val() as Record<string, unknown>;
        const validDrafts: { [key: string]: ReportDraft } = {};
        for (const [key, value] of Object.entries(rawData)) {
            const parsed = this.parseDraft(value, key);
            if (parsed) validDrafts[key] = parsed;
        }
        return validDrafts;
    }

    /**
     * Fetch a single draft
     */
    async getDraft(draftKey: string): Promise<ReportDraft | null> {
        this.getCurrentUser();

        const snapshot = await get(ref(this.db, this.getDraftPath(draftKey)));
        if (!snapshot.exists()) return null;
        return this.parseDraft(snapshot.val(), draftKey);
    }

    /**
     * Create a draft with seeded membership from the filter step
     */
    async createDraft(
        name: string,
        filters: ReportDraftFilters,
        seedItems: { [eventId: string]: ReportDraftItem }
    ): Promise<ReportDraft> {
        const user = this.getCurrentUser();
        if (!name.trim()) {
            throw new Error('Utkastet måste ha ett namn');
        }

        const newRef = push(ref(this.db, DRAFTS_PATH));
        const draft: ReportDraft = {
            key: newRef.key!,
            name: name.trim(),
            reportTitle: 'Tidslinjerapport',
            filters,
            items: seedItems,
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date()),
        };

        await set(newRef, stripUndefined(draft));
        recordActivity('report.created', { targetKey: draft.key, label: draft.name });
        return draft;
    }

    /**
     * Duplicate a draft as a starting point (e.g. next year's report)
     */
    async duplicateDraft(sourceDraftKey: string, newName: string): Promise<ReportDraft> {
        const user = this.getCurrentUser();
        if (!newName.trim()) {
            throw new Error('Utkastet måste ha ett namn');
        }

        const source = await this.getDraft(sourceDraftKey);
        if (!source) {
            throw new Error('Utkastet som ska kopieras finns inte längre');
        }

        const newRef = push(ref(this.db, DRAFTS_PATH));
        const copy: ReportDraft = {
            ...source,
            key: newRef.key!,
            name: newName.trim(),
            archived: false,
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date()),
            updatedBy: undefined,
            updatedAt: undefined,
        };

        await set(newRef, stripUndefined(copy));
        recordActivity('report.created', { targetKey: copy.key, label: copy.name });
        return copy;
    }

    /**
     * Rename a draft (internal list name)
     */
    async renameDraft(draftKey: string, name: string): Promise<void> {
        const user = this.getCurrentUser();
        if (!name.trim()) {
            throw new Error('Utkastet måste ha ett namn');
        }
        await update(ref(this.db, this.getDraftPath(draftKey)), {
            name: name.trim(),
            ...this.stamp(user),
        });
        recordActivity('report.updated', { targetKey: draftKey, label: name.trim() });
    }

    /**
     * Update the printed report title. Filters are fixed at creation and have
     * no update path — the draft's scope never changes silently.
     */
    async updateDraftMeta(
        draftKey: string,
        meta: { reportTitle: string }
    ): Promise<void> {
        const user = this.getCurrentUser();
        if (!meta.reportTitle.trim()) {
            throw new Error('Rapporten måste ha en titel');
        }
        await this.assertDraftExists(draftKey);
        await update(ref(this.db, this.getDraftPath(draftKey)), {
            reportTitle: meta.reportTitle.trim(),
            ...this.stamp(user),
        });
        recordActivity('report.updated', { targetKey: draftKey, label: meta.reportTitle.trim() });
    }

    /**
     * Archive or unarchive a draft
     */
    async setArchived(draftKey: string, archived: boolean): Promise<void> {
        const user = this.getCurrentUser();
        await update(ref(this.db, this.getDraftPath(draftKey)), {
            archived,
            ...this.stamp(user),
        });
        recordActivity(archived ? 'report.archived' : 'report.updated', { targetKey: draftKey });
    }

    /**
     * Delete a draft permanently
     */
    async deleteDraft(draftKey: string): Promise<void> {
        this.getCurrentUser();
        await set(ref(this.db, this.getDraftPath(draftKey)), null);
        recordActivity('report.deleted', { targetKey: draftKey });
    }

    /**
     * Write one member item (curation state for one event)
     */
    async setItem(draftKey: string, eventId: string, item: ReportDraftItem): Promise<void> {
        const user = this.getCurrentUser();
        await this.assertDraftExists(draftKey);
        await update(ref(this.db, this.getDraftPath(draftKey)), {
            [`items/${eventId}`]: stripUndefined(item),
            ...this.stamp(user),
        });
        recordActivity('report.updated', { targetKey: draftKey });
    }

    /**
     * Mint a Firebase auto-ID for a new text block (synchronous, no network) —
     * lets callers keep optimistic state and the eventual write on one key.
     */
    newTextBlockKey(draftKey: string): string {
        return push(ref(this.db, `${this.getDraftPath(draftKey)}/textBlocks`)).key!;
    }

    /**
     * Create or update a text block. New blocks get their key from
     * newTextBlockKey() so callers can keep optimistic state on the same key.
     * Empty text is allowed (a block being typed) — the PDF skips blank blocks.
     */
    async setTextBlock(draftKey: string, block: ReportDraftTextBlock): Promise<void> {
        const user = this.getCurrentUser();
        await this.assertDraftExists(draftKey);
        await update(ref(this.db, this.getDraftPath(draftKey)), {
            [`textBlocks/${block.key}`]: stripUndefined(block),
            ...this.stamp(user),
        });
        recordActivity('report.updated', { targetKey: draftKey });
    }

    /**
     * Remove a text block
     */
    async removeTextBlock(draftKey: string, blockKey: string): Promise<void> {
        const user = this.getCurrentUser();
        await this.assertDraftExists(draftKey);
        await update(ref(this.db, this.getDraftPath(draftKey)), {
            [`textBlocks/${blockKey}`]: null,
            ...this.stamp(user),
        });
        recordActivity('report.updated', { targetKey: draftKey });
    }
}

// Export singleton instance
export const reportDraftsApi = new ReportDraftsApi();
